/**
 * Email API Service Functions
 */
import { apiClient, apiGet } from "@/lib/api-client";
import type {
    EmailAccountResponse,
    EmailAccountCreate,
    EmailAccountCreateIMAP,
    EmailAccountUpdate,
    EmailAccountUpdateIMAP,
    EmailAccountFilters,
    OAuthAuthorizationResponse,
    ProviderPreset,
    ConnectionTestResponse,
    ProcessEmailsResponse,
    EmailMessageResponse,
    EmailMessageDetailResponse,
    EmailMessageFilters,
    EmailFolderInfo,
    EmailCountsResponse,
} from "@/types/api/email";

// ============================================================================
// Email Accounts
// ============================================================================

/**
 * Get list of email accounts with optional filters
 */
export async function getEmailAccounts(
    filters?: EmailAccountFilters
): Promise<EmailAccountResponse[]> {
    const params: Record<string, string | number> = {};

    if (filters?.skip !== undefined) params.skip = filters.skip;
    if (filters?.limit !== undefined) params.limit = filters.limit;
    if (filters?.status) params.status = filters.status;

    return apiClient<EmailAccountResponse[]>("/email/accounts", {
        method: "GET",
        params,
    });
}

/**
 * Get a single email account by ID
 */
export async function getEmailAccount(
    accountId: string
): Promise<EmailAccountResponse> {
    return apiClient<EmailAccountResponse>(`/email/accounts/${accountId}`, {
        method: "GET",
    });
}

/**
 * Get available folders/labels for an email account
 */
export async function getEmailAccountFolders(
    accountId: string
): Promise<EmailFolderInfo[]> {
    return apiClient<EmailFolderInfo[]>(`/email/accounts/${accountId}/folders`, {
        method: "GET",
    });
}

/**
 * Create a new OAuth email account (Gmail/Outlook)
 */
export async function createEmailAccount(
    data: EmailAccountCreate
): Promise<EmailAccountResponse> {
    return apiClient<EmailAccountResponse>("/email/accounts", {
        method: "POST",
        body: JSON.stringify(data),
    });
}

/**
 * Create a new IMAP/SMTP email account
 */
export async function createImapEmailAccount(
    data: EmailAccountCreateIMAP
): Promise<EmailAccountResponse> {
    return apiClient<EmailAccountResponse>("/email/accounts/imap", {
        method: "POST",
        body: JSON.stringify(data),
    });
}

/**
 * Update an OAuth email account
 */
export async function updateEmailAccount(
    accountId: string,
    data: EmailAccountUpdate
): Promise<EmailAccountResponse> {
    return apiClient<EmailAccountResponse>(`/email/accounts/${accountId}`, {
        method: "PUT",
        body: JSON.stringify(data),
    });
}

/**
 * Update an IMAP/SMTP email account
 */
export async function updateImapEmailAccount(
    accountId: string,
    data: EmailAccountUpdateIMAP
): Promise<EmailAccountResponse> {
    return apiClient<EmailAccountResponse>(`/email/accounts/imap/${accountId}`, {
        method: "PUT",
        body: JSON.stringify(data),
    });
}

/**
 * Delete an email account (soft delete)
 */
export async function deleteEmailAccount(accountId: string): Promise<void> {
    return apiClient<void>(`/email/accounts/${accountId}`, {
        method: "DELETE",
    });
}

// ============================================================================
// OAuth Flow
// ============================================================================

/**
 * Get Gmail OAuth authorization URL
 */
export async function getGmailAuthorizationUrl(
    accountId: string
): Promise<OAuthAuthorizationResponse> {
    return apiClient<OAuthAuthorizationResponse>(
        `/email/accounts/${accountId}/oauth/authorize`,
        { method: "GET" }
    );
}

/**
 * Get Outlook OAuth authorization URL
 */
export async function getOutlookAuthorizationUrl(
    accountId: string
): Promise<OAuthAuthorizationResponse> {
    return apiClient<OAuthAuthorizationResponse>(
        `/email/accounts/${accountId}/oauth/authorize/outlook`,
        { method: "GET" }
    );
}

/**
 * Refresh Gmail OAuth token
 */
export async function refreshGmailToken(
    accountId: string
): Promise<{ status: string; message: string }> {
    return apiClient<{ status: string; message: string }>(
        `/email/accounts/${accountId}/oauth/refresh`,
        { method: "POST" }
    );
}

/**
 * Refresh Outlook OAuth token
 */
export async function refreshOutlookToken(
    accountId: string
): Promise<{ status: string; message: string }> {
    return apiClient<{ status: string; message: string }>(
        `/email/accounts/${accountId}/oauth/refresh/outlook`,
        { method: "POST" }
    );
}

// ============================================================================
// IMAP Provider Presets
// ============================================================================

/**
 * Get all IMAP provider presets
 */
export async function getImapProviderPresets(): Promise<ProviderPreset[]> {
    return apiClient<ProviderPreset[]>("/email/providers/imap/presets", {
        method: "GET",
    });
}

/**
 * Get a specific IMAP provider preset
 */
export async function getImapProviderPreset(
    presetName: string
): Promise<ProviderPreset> {
    return apiClient<ProviderPreset>(
        `/email/providers/imap/presets/${presetName}`,
        { method: "GET" }
    );
}

// ============================================================================
// Connection Test
// ============================================================================

/**
 * Test IMAP/SMTP connection for an account
 */
export async function testEmailConnection(
    accountId: string
): Promise<ConnectionTestResponse> {
    return apiClient<ConnectionTestResponse>(
        `/email/accounts/${accountId}/test-connection`,
        { method: "POST" }
    );
}

// ============================================================================
// Email Processing
// ============================================================================

/**
 * Process emails for a specific account
 */
export async function processAccountEmails(
    accountId: string
): Promise<ProcessEmailsResponse> {
    return apiClient<ProcessEmailsResponse>(
        `/email/accounts/${accountId}/process`,
        { method: "POST" }
    );
}

/**
 * Process emails for all active accounts
 */
export async function processAllEmails(): Promise<{
    accounts_processed: number;
    results: ProcessEmailsResponse[];
}> {
    return apiClient<{ accounts_processed: number; results: ProcessEmailsResponse[] }>(
        "/email/process-all",
        { method: "POST" }
    );
}

// ============================================================================
// Email Messages (History)
// ============================================================================

/**
 * Get list of email messages with optional filters
 */
export async function getEmailMessages(
    filters?: EmailMessageFilters
): Promise<EmailMessageResponse[]> {
    const params: Record<string, string | number> = {};

    if (filters?.skip !== undefined) params.skip = filters.skip;
    if (filters?.limit !== undefined) params.limit = filters.limit;
    if (filters?.status) params.status = filters.status;
    if (filters?.account_id) params.account_id = filters.account_id;

    return apiClient<EmailMessageResponse[]>("/email/messages", {
        method: "GET",
        params,
    });
}

/**
 * Get a single email message with full details and attachments
 */
export async function getEmailMessage(
    messageId: string
): Promise<EmailMessageDetailResponse> {
    return apiClient<EmailMessageDetailResponse>(`/email/messages/${messageId}`, {
        method: "GET",
    });
}

// ============================================================================
// Provider Detection (MX Records)
// ============================================================================

export interface EmailProviderDetectionResult {
    email: string;
    domain: string;
    detected_provider: "gmail" | "outlook" | null;
    provider_name: string;
    confidence: "high" | "low";
    mx_records: string[];
}

/**
 * Detect email provider via DNS MX records lookup.
 * This can detect Google Workspace and Microsoft 365 on custom domains.
 */
export async function detectEmailProviderFromMX(
    email: string
): Promise<EmailProviderDetectionResult> {
    return apiClient<EmailProviderDetectionResult>("/email/detect-provider", {
        method: "GET",
        params: { email },
    });
}

// ============================================================================
// Helper Functions
// ============================================================================

/**
 * Detect email provider from email address domain (local, no API call)
 * @deprecated Use detectEmailProviderFromMX for better accuracy with custom domains
 */
export function detectProviderFromEmail(
    email: string
): "gmail" | "outlook" | "imap_smtp" | null {
    const domain = email.split("@")[1]?.toLowerCase();
    if (!domain) return null;

    // Gmail domains
    if (domain === "gmail.com" || domain === "googlemail.com") {
        return "gmail";
    }

    // Microsoft domains
    if (
        [
            "outlook.com",
            "hotmail.com",
            "live.com",
            "msn.com",
            "microsoft.com",
        ].includes(domain)
    ) {
        return "outlook";
    }

    // Everything else uses IMAP/SMTP
    return "imap_smtp";
}

/**
 * Get provider display name
 */
export function getProviderDisplayName(provider: string): string {
    switch (provider) {
        case "gmail":
            return "Gmail";
        case "outlook":
            return "Outlook";
        case "imap_smtp":
            return "IMAP/SMTP";
        default:
            return provider;
    }
}

/**
 * Get total email counts across all accounts
 * Returns unread inbox count, limbo count, and per-account breakdown
 */
export async function fetchEmailCounts(): Promise<EmailCountsResponse> {
    return apiGet<EmailCountsResponse>("/email/counts");
}
